import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';

import { datePadStart, formatDateFreeDrop, formatFreeDropRemainingTime, getDateInfo } from '../../../../../../helpers';
import { useStore, useTranslation } from '../../../../../../hooks';
import { T } from '../../../../../../constants';

interface DetailsModalHeaderProps {
  nextDropDate: Date | null;
}

export const DetailsModalHeader: React.FC<DetailsModalHeaderProps> = observer(({ nextDropDate }) => {

  const t = useTranslation();
  const { freeDropStore } = useStore();
  const { sortedDropsByDate } = freeDropStore;

  const [nextDropRemainingTime, setNextDropRemainingTime] = useState(
    nextDropDate ? formatFreeDropRemainingTime(nextDropDate) : ''
  );

  useEffect(() => {
    let interval: NodeJS.Timer;
    if (nextDropDate) {
      interval = setInterval(() => {
        setNextDropRemainingTime(formatFreeDropRemainingTime(nextDropDate));
      }, 1000);
    }
    return () => {
      clearInterval(interval);
    };
  }, [nextDropDate]);

  const closePopup = useCallback(() => {
    freeDropStore.setShowDetailsPopup(false);
  }, []);

  const dropInfo = useMemo(() => {
    const firstDropDate = new Date(sortedDropsByDate[0].dropDate);
    const lastDropDate = new Date(sortedDropsByDate[sortedDropsByDate.length - 1].dropDate);
    const firstDropDateInfo = getDateInfo(firstDropDate);
    const lastDropDateInfo = getDateInfo(lastDropDate);
    return {
      first: {
        date: formatDateFreeDrop(firstDropDate),
        hour: datePadStart(firstDropDateInfo.hour),
        minute: datePadStart(firstDropDateInfo.minute)
      },
      last: {
        date: formatDateFreeDrop(lastDropDate),
        hour: datePadStart(lastDropDateInfo.hour),
        minute: datePadStart(lastDropDateInfo.minute)
      }
    };
  }, [sortedDropsByDate]);

  return (
    <div className="free-drop-popup-header">
      <div className="free-drop-popup-header-content">
        <p className="free-drop-popup-header-title">{t(T.FREE_DROP)}</p>
        <p className="free-drop-popup-header-subtitle">
          {t(T.FREE_DROP_IS_LIVE)}
          <br />
          {t(T.EVERY_BET_ADDS_ENERGY)}
        </p>
        <div className="free-drop-popup-header-date-wrp">
          <div className="free-drop-popup-header-date-holder">
            <p className="free-dr-date-clm-info">{dropInfo.first.date}</p>
            <p className="free-dr-date-clm-info">{dropInfo.last.date}</p>
          </div>
          <div className="free-drop-popup-header-date-inner">
            <div className="free-drop-popup-header-date-clm">
              <div className="free-drop-popup-header-date-clm-item">
                <span className="free-drop-popup-header-date-info">{dropInfo.first.hour}</span>
              </div>
              <span className="free-drop-popup-header-date-dot">:</span>
              <div className="free-drop-popup-header-date-clm-item">
                <span className="free-drop-popup-header-date-info">{dropInfo.first.minute}</span>
              </div>
            </div>
            <span className="free-drop-popup-header-date-line" />
            <div className="free-drop-popup-header-date-clm">
              <div className="free-drop-popup-header-date-clm-item">
                <span className="free-drop-popup-header-date-info">{dropInfo.last.hour}</span>
              </div>
              <span className="free-drop-popup-header-date-dot">:</span>
              <div className="free-drop-popup-header-date-clm-item">
                <span className="free-drop-popup-header-date-info">{dropInfo.last.minute}</span>
              </div>
            </div>
          </div>
        </div>
        <p className="free-drop-popup-header-time-info">
          {t(T.NEXT_DROP_IN)} {nextDropRemainingTime}
        </p>
      </div>
      <div className="free-drop-popup-close-block" onClick={closePopup}>
        <i className="icon-close free-drop-popup-close" />
      </div>
    </div>
  );
});
